import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Trophy, Crown, ShareNetwork } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin → Affiliates tab.
 * Shows the referral program leaderboard + platform-wide stats. Fully local
 * state, fetched on mount.
 */
const AffiliatesTab = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAffiliates = useCallback(async () => {
    setLoading(true);
    try {
      const [lb, st] = await Promise.all([
        axios.get(`${API}/api/admin/referrals/leaderboard`, { withCredentials: true }),
        axios.get(`${API}/api/admin/referrals/stats`, { withCredentials: true }),
      ]);
      setLeaderboard(lb.data);
      setStats(st.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAffiliates(); }, [fetchAffiliates]);

  if (loading) {
    return (
      <div data-testid="affiliates-tab-content">
        <div className="text-center py-16">
          <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="affiliates-tab-content">
      {/* Platform stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { label: 'Total Referrals', value: stats?.total_referrals || 0, color: 'text-blue-400' },
          { label: 'Active', value: stats?.active_referrals || 0, color: 'text-emerald-400' },
          { label: 'Unique Referrers', value: stats?.unique_referrers || 0, color: 'text-indigo-400' },
          { label: 'Tokens Paid Out', value: stats?.total_commissions_tokens || 0, color: 'text-amber-400' },
          { label: 'Commission Events', value: stats?.total_commission_events || 0, color: 'text-blue-400' },
        ].map(s => (
          <div key={s.label} className="glass-light rounded-xl p-4">
            <p className="text-slate-500 text-xs mb-1">{s.label}</p>
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Leaderboard */}
      <div className="glass-light rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
          <Trophy weight="fill" className="w-5 h-5 text-amber-400" />
          <h3 className="text-white font-bold">Top Affiliates</h3>
        </div>

        {leaderboard.length === 0 ? (
          <div className="p-8 text-center">
            <ShareNetwork className="w-12 h-12 text-slate-700 mx-auto mb-2" />
            <p className="text-slate-500 text-sm">No referrals yet</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {leaderboard.map((entry, idx) => (
              <div key={entry.user_id} className="px-5 py-4 flex items-center gap-4 hover:bg-white/[0.02]" data-testid={`affiliate-rank-${idx + 1}`}>
                <div className="w-8 text-center flex-shrink-0">
                  {idx === 0 ? <Crown weight="fill" className="w-6 h-6 text-amber-400 mx-auto" /> :
                   idx === 1 ? <span className="text-slate-300 font-bold text-lg">2</span> :
                   idx === 2 ? <span className="text-orange-400 font-bold text-lg">3</span> :
                   <span className="text-slate-600 font-bold">#{idx + 1}</span>}
                </div>
                <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {entry.avatar_url ? <img src={entry.avatar_url} alt="" className="w-full h-full object-cover" /> :
                    <span className="text-slate-500 font-bold text-sm">{(entry.display_name || '?')[0]}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm truncate">{entry.display_name}</p>
                  <p className="text-slate-500 text-xs flex items-center gap-2">
                    <span className="capitalize">{entry.role}</span>
                    <span>&middot;</span>
                    <span className="font-mono text-emerald-400">{entry.referral_code}</span>
                  </p>
                </div>
                <div className="text-right flex-shrink-0 space-y-0.5">
                  <p className="text-white font-bold text-sm">{entry.total_referrals} referrals</p>
                  <p className="text-amber-400 text-xs font-semibold">{entry.total_earnings_tokens} tokens earned</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AffiliatesTab;
